// Meldinger til de ansatte under «Finn kunde» på kontrollpanelet (src/routes/ansattmeldinger.js).
// Ett banner med en kø (Anders 03.11): den eldste uleste vises først, «1 av 3» med ‹ › for å se
// de andre, og «Merk som lest» tar fram den neste. Kildene står i KILDE: beskjeder fra klubbinfo
// der «Vis på kontrollpanelet» er huket av (vinnsel), og nyheter fra leder (Ansatte → Nyheter).
// Hver ansatt merker meldingen som lest, og det registreres på serveren (lestAv), så leder ser
// hvem som har lest og ikke. Klubbmeldinger vises bare i butikken det er logget inn i, nyheter
// bare til mottakerne (serveren avgjør begge deler).
//
// Meldingskort er selve kortet, også brukt som forhåndsvisning på Nyheter-siden.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Ikon from './Ikon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ansattHeader = (navn) => ({ 'X-Ansatt': encodeURIComponent(navn || '') });
const formatDato = (d) => (d ? new Date(d).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }) : '');

// Ny kilde: legg den til her og i KILDER i src/model/Ansattmelding.js.
export const KILDE = {
  klubb: { navn: 'Klubbinfo', ikon: 'folk', lenketekst: 'Se klubbsiden' },
  nyhet: { navn: 'Nyhet', ikon: 'melding', lenketekst: 'Les mer' },
};

const knapp =
  'min-h-[2.25rem] shrink-0 rounded-xl border-2 border-sky-300 bg-white px-3 py-1.5 font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-60 disabled:hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';

// tekst er ren tekst: tom linje gir nytt avsnitt, linjeskift beholdes.
export function Meldingskort({ melding, id, forhandsvisning = false, tittelRef, topp, children }) {
  const kilde = KILDE[melding.kilde] || { navn: 'Melding', ikon: 'melding', lenketekst: 'Les mer' };
  const Boks = forhandsvisning ? 'div' : 'aside';
  const Tittel = forhandsvisning ? 'p' : 'h2';
  const avsnitt = melding.tekst ? String(melding.tekst).split(/\n\s*\n/).filter((a) => a.trim()) : [];
  return (
    <Boks aria-labelledby={forhandsvisning ? undefined : id} className="space-y-2 rounded-2xl border border-sky-200 bg-sky-50 p-3 text-slate-800">
      <div className="flex flex-wrap items-start gap-2">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-700 text-white">
          <Ikon navn={kilde.ikon} className="w-6 h-6" />
        </span>
        <div className="min-w-0 flex-1 basis-60 space-y-1 [overflow-wrap:anywhere]">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <p className="text-sm font-semibold text-sky-900">
              {kilde.navn}{melding.fra ? `: ${melding.fra}` : ''}
              <span className="font-normal text-slate-600"> · {formatDato(melding.createdAt || Date.now())}</span>
            </p>
            {topp}
          </div>
          <Tittel id={id} ref={tittelRef} tabIndex={tittelRef ? -1 : undefined} className="font-bold text-slate-900 focus:outline-none">
            {melding.tittel}
          </Tittel>
          {avsnitt.map((a, i) => <p key={i} className="whitespace-pre-line">{a.trim()}</p>)}
          {melding.lenke && (
            <p>
              <Link to={melding.lenke} className="font-semibold text-indigo-700 underline underline-offset-2">
                {kilde.lenketekst}<span className="sr-only">: {melding.tittel}</span>
              </Link>
            </p>
          )}
          {melding.hilsen && <p className="font-medium">{melding.hilsen}</p>}
        </div>
      </div>
      {children}
    </Boks>
  );
}

export default function Ansattmeldinger({ ansatt, butikk }) {
  const [meldinger, setMeldinger] = useState([]);
  const [indeks, setIndeks] = useState(0);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const tittelRef = useRef(null);
  const flyttFokus = useRef(false);

  const hent = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/ansattmeldinger`, { headers: ansattHeader(ansatt) });
      if (!r.ok) return;
      const liste = await r.json();
      // Eldste først: køen tas i den rekkefølgen meldingene kom.
      setMeldinger(liste.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    } catch (e) {
      /* prøver igjen ved neste henting */
    }
  }, [ansatt]);

  // Hentes på nytt når ansatt eller butikk byttes, og hvert 5. minutt.
  useEffect(() => {
    setIndeks(0);
    hent();
    const id = setInterval(hent, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [hent, butikk]);

  const antall = meldinger.length;
  const i = Math.min(indeks, Math.max(antall - 1, 0));
  const melding = meldinger[i];

  // Etter «Merk som lest» og bla: fokus på overskriften i meldingen som nå vises, så tastatur- og
  // skjermleserbrukere ikke mister plassen.
  useEffect(() => {
    if (flyttFokus.current && melding) tittelRef.current?.focus();
    flyttFokus.current = false;
  }, [melding]);

  if (!melding) return null;

  function bla(steg) {
    setFeil('');
    flyttFokus.current = true;
    setIndeks(Math.min(Math.max(i + steg, 0), antall - 1));
  }

  async function merkLest() {
    setLagrer(true);
    setFeil('');
    try {
      const r = await fetch(`${API_BASE_URL}/ansattmeldinger/${melding._id}/lest`, { method: 'POST', headers: ansattHeader(ansatt) });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.message || 'Kunne ikke merke meldingen som lest');
      // Den neste glir inn på samme plass (eller den forrige, om dette var den siste).
      flyttFokus.current = true;
      setMeldinger((l) => l.filter((x) => x._id !== melding._id));
      setIndeks(Math.max(0, Math.min(i, antall - 2)));
    } catch (err) {
      setFeil(err.message);
    } finally {
      setLagrer(false);
    }
  }

  const id = `nk-melding-${melding._id}`;
  const topp = antall > 1 && (
    <div className="flex items-center gap-1" role="group" aria-label="Bla i meldingene">
      <button type="button" onClick={() => bla(-1)} disabled={i === 0} aria-label="Forrige melding" className={`${knapp} px-2.5`}>
        <span aria-hidden="true">‹</span>
      </button>
      <p className="min-w-[3.5rem] text-center text-sm font-semibold text-slate-700" aria-live="polite">
        <span className="sr-only">Melding </span>{i + 1} av {antall}
      </p>
      <button type="button" onClick={() => bla(1)} disabled={i === antall - 1} aria-label="Neste melding" className={`${knapp} px-2.5`}>
        <span aria-hidden="true">›</span>
      </button>
    </div>
  );

  return (
    <Meldingskort melding={melding} id={id} tittelRef={tittelRef} topp={topp}>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {feil && <p role="alert" className="mr-auto font-semibold text-rose-800">{feil}</p>}
        {ansatt ? (
          <button type="button" onClick={merkLest} disabled={lagrer} className={knapp}>
            {lagrer ? 'Lagrer …' : 'Merk som lest'}<span className="sr-only">: {melding.tittel}</span>
          </button>
        ) : (
          <p className="text-sm text-slate-700">Velg deg selv øverst for å merke som lest.</p>
        )}
      </div>
    </Meldingskort>
  );
}
